/**
 * Tiny dev-only logger for the Sales surface. Every layout, page,
 * client call, and error funnels through this so a developer can
 * open devtools and follow the agent's roundtrip without litering
 * the codebase with raw `console.log`.
 *
 * Usage:
 *   devLog("today/mount");
 *   devLog("today/fetch:start");
 *   devLog("today/fetch:ok", { fresh_leads: data.fresh_leads.length });
 *   devLog("today/fetch:error", { code: err.code });
 *
 * Production builds short-circuit the entire function on the very
 * first line — Vite's dead-code elimination then drops every call
 * site so we pay no runtime cost in prod.
 *
 * The flag also respects an explicit override:
 *   localStorage.setItem("xop.sales.debug", "1")  // force-on
 *   localStorage.setItem("xop.sales.debug", "0")  // force-off
 * so a staff user can flip dev logging on against a prod build for
 * one-off debugging without touching the deploy.
 */
const TAG = "[sales]";

function debugEnabled(): boolean {
  // import.meta.env is statically replaced at build time by Vite, so
  // this whole branch becomes `if (false)` in prod and the body of
  // every devLog call below it gets stripped by the minifier.
  if (typeof import.meta !== "undefined" && import.meta.env?.DEV) return true;

  if (typeof window !== "undefined" && typeof window.localStorage !== "undefined") {
    try {
      const v = window.localStorage.getItem("xop.sales.debug");
      if (v === "1" || v === "true") return true;
      if (v === "0" || v === "false") return false;
    } catch {
      // localStorage can throw in private mode; ignore.
    }
  }
  return false;
}

export function devLog(event: string, payload?: unknown): void {
  if (!debugEnabled()) return;
  if (payload === undefined) {
    // eslint-disable-next-line no-console
    console.log(`${TAG} ${event}`);
  } else {
    // eslint-disable-next-line no-console
    console.log(`${TAG} ${event}`, payload);
  }
}

export function devError(event: string, error: unknown): void {
  if (!debugEnabled()) return;
  // eslint-disable-next-line no-console
  console.error(`${TAG} ${event}`, error);
}

export function devGroup<T>(event: string, fn: () => T): T {
  if (!debugEnabled()) return fn();
  // eslint-disable-next-line no-console
  console.groupCollapsed(`${TAG} ${event}`);
  try {
    return fn();
  } finally {
    // eslint-disable-next-line no-console
    console.groupEnd();
  }
}
